import classnames from 'classnames/bind';

import { Camera } from 'types/camera';

import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteReferencePipelines } from 'cameras/hooks/useDeleteReferencePipelines';
import { useDeleteReferenceDeployments } from 'cameras/hooks/useDeleteReferenceDeployments';
import { usePipelines } from 'pipelines/hooks/usePipelines';
import { useDeployments } from 'deployments/hooks/useDeployments';

import { Accordion } from 'components/Accordion/Accordion';
import { AppLink } from 'components/AppLink/AppLink';
import { Heading } from 'components/Heading/Heading';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';

import { UniversalBridgeDeployments } from './UniversalBridgeDeployments';

import styles from './CameraDetailSettings.module.scss';

const c = classnames.bind(styles);

type UniversalBridgePipelinesProps = {
  camera: Camera;
};

export function UniversalBridgePipelines({
  camera,
}: UniversalBridgePipelinesProps) {
  const isEnabled = Boolean(camera.reference_pipeline_ids.length);
  const { isSimplifiedUxPlan } = useCurrentPlan();

  const { data: pipelines, isInitialLoading: isLoading } = usePipelines({
    params: {
      pipeline_ids: camera.reference_pipeline_ids,
      limit: camera.reference_pipeline_ids.length,
    },
    enabled: isEnabled,
    keepPreviousData: true,
  });

  const { mutate: deleteReferencePipelines } = useDeleteReferencePipelines();
  const { mutate: deleteReferenceDeployments } =
    useDeleteReferenceDeployments();

  // Fetch all reference deployments to find which ones belong to each pipeline
  const { data: allReferenceDeployments } = useDeployments({
    params: {
      deployment_ids: camera.reference_deployment_ids,
      limit: camera.reference_deployment_ids.length,
    },
    enabled: camera.reference_deployment_ids.length > 0,
    keepPreviousData: true,
  });

  function handleDeleteReferencePipeline(pipelineId: string) {
    // Find all deployments that use this pipeline
    const deploymentIdsToDelete =
      allReferenceDeployments?.data
        .filter((deployment) => deployment.pipeline_id === pipelineId)
        .map((deployment) => deployment.id) ?? [];

    // Delete the deployments first, then delete the pipeline
    if (deploymentIdsToDelete.length > 0) {
      deleteReferenceDeployments({
        camera_id: camera.id,
        deployment_ids: deploymentIdsToDelete,
      });
    }

    deleteReferencePipelines({
      camera_id: camera.id,
      pipeline_ids: [pipelineId],
    });
  }

  if (!pipelines && isLoading) {
    return (
      <SkeletonList
        component={<UniversalBridgePipelineSkeletonItem />}
        min={2}
        max={5}
      />
    );
  }

  if (!isEnabled) {
    return null;
  }

  return (
    <div className={c('list')}>
      {pipelines?.data.map(({ id, name }) => (
        <Accordion
          key={id}
          label={
            <div className={c('item')}>
              <Heading level="4" asChild>
                {isSimplifiedUxPlan ? (
                  <Text type="paragraph">{name}</Text>
                ) : (
                  <AppLink to={`/design/pipelines/${id}`}>{name}</AppLink>
                )}
              </Heading>
              <IconButton
                icon="delete"
                intent="danger"
                size="small"
                label="Remove workflow"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteReferencePipeline(id);
                }}
              />
            </div>
          }
          icon="pipeline"
          level={4}
          defaultOpen={true}
        >
          <UniversalBridgeDeployments camera={camera} pipeline_id={id} />
        </Accordion>
      ))}
    </div>
  );
}

function UniversalBridgePipelineSkeletonItem() {
  return (
    <div
      className={c('item', 'loading', 'skeleton')}
      aria-label="Loading camera workflows..."
    >
      <Text className={c('skeleton-text')}>Loading workflow...</Text>
      <Text className={c('skeleton-text')} />
    </div>
  );
}
